import { FC, useState } from 'react';
import OrganizationCostChart from '@pages/back-office/organization-cost-chart.tsx';
import UsageMetricsChart from '@pages/back-office/usage-metrics-chart.tsx';
import CostDistributionChart from '@pages/back-office/cost-distribution-chart.tsx';
import { UsageBo } from '@utils/api/back-office/usage/use-get-usage-bo.tsx';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

interface UsageAnalyticsDashboardProps {
  data: UsageBo[];
  periodFilter: string;
  setPeriodFilter: (filter: string) => void;
}

const UsageAnalyticsDashboard: FC<UsageAnalyticsDashboardProps> = ({
  data,
  periodFilter,
  setPeriodFilter
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState('all');
  const { getColour } = useThemeContext();

  const cardClass =
    'rounded-2xl border border-hairline bg-app-soft shadow-card hover:shadow-card-hover transition-shadow';
  const cardPadding = 'p-6';

  const totalCost = data.reduce(
    (sum, org) => sum + (parseFloat(org.total_cost) || 0),
    0
  );
  const totalSttMinutes = data.reduce((sum, org) => sum + org.stt_units, 0);
  const totalOrganizations = data.length;
  const averageCostPerOrg = totalOrganizations
    ? totalCost / totalOrganizations
    : 0;

  const formattedTotalCost = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(totalCost);

  const formattedAvgCost = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(averageCostPerOrg);

  const filterSelectClass =
    'border border-hairline bg-app-soft rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 transition-all';

  return (
    <div className="w-full" style={{ color: getColour('normalText') }}>
      {/* Filters and Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 my-6">
        <div className="flex flex-wrap gap-2">
          <select
            className={filterSelectClass}
            style={{ color: getColour('normalText') }}
            value={periodFilter}
            onChange={e => setPeriodFilter(e.target.value)}
          >
            <option value="current">{t('current-month')}</option>
            <option value="lastMonth">{t('last-month')}</option>
            <option value="quarter">{t('last-quarter')}</option>
            <option value="year">{t('last-year')}</option>
          </select>

          <select
            className={filterSelectClass}
            style={{ color: getColour('normalText') }}
            value={view}
            onChange={e => setView(e.target.value)}
          >
            <option value="all">{t('all-charts')}</option>
            <option value="cost">{t('cost-analysis')}</option>
            <option value="usage">{t('usage-metrics')}</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 px-2">
        {/* Summary (STT + Cost) */}
        <div className={`${cardClass} ${cardPadding}`}>
          <h3
            className="text-xs font-medium uppercase tracking-wide mb-3"
            style={{ color: getColour('inactiveText') }}
          >
            {t('summary')}
          </h3>

          <div className="grid grid-cols-2 items-center">
            <div className="pr-4">
              <p
                className="text-2xl font-bold"
                style={{ color: getColour('activeText') }}
              >
                {totalSttMinutes.toLocaleString()}
              </p>
              <p
                className="text-xs mt-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('total-stt-minutes')}
              </p>
            </div>

            <div
              className="pl-4 border-l border-hairline"
              style={{ color: getColour('normalText') }}
            >
              <p
                className="text-2xl font-bold"
                style={{ color: getColour('activeText') }}
              >
                {formattedTotalCost}
              </p>
              <p
                className="text-xs mt-1"
                style={{ color: getColour('inactiveText') }}
              >
                {t('total-cost')}
              </p>
            </div>
          </div>
        </div>

        {/* Active Orgs */}
        <div className={`${cardClass} ${cardPadding}`}>
          <h3
            className="text-xs font-medium uppercase tracking-wide mb-3"
            style={{ color: getColour('inactiveText') }}
          >
            {t('active-organizations')}
          </h3>
          <p
            className="text-2xl font-bold"
            style={{ color: getColour('activeText') }}
          >
            {totalOrganizations}
          </p>
        </div>

        {/* Avg Cost */}
        <div className={`${cardClass} ${cardPadding}`}>
          <h3
            className="text-xs font-medium uppercase tracking-wide mb-3"
            style={{ color: getColour('inactiveText') }}
          >
            {t('avg-cost-per-org')}
          </h3>
          <p
            className="text-2xl font-bold"
            style={{ color: getColour('activeText') }}
          >
            {formattedAvgCost}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div
        className={`grid gap-6 px-2 ${
          view === 'all'
            ? 'grid-cols-1 xl:grid-cols-3'
            : view === 'cost'
              ? 'grid-cols-1 lg:grid-cols-2'
              : 'grid-cols-1'
        }`}
      >
        {(view === 'all' || view === 'cost') && (
          <div className={`${cardClass} ${cardPadding}`}>
            <CostDistributionChart data={data} />
          </div>
        )}

        {(view === 'all' || view === 'cost') && (
          <div className={`${cardClass} ${cardPadding}`}>
            <OrganizationCostChart data={data} />
          </div>
        )}

        {(view === 'all' || view === 'usage') && (
          <div className={`${cardClass} ${cardPadding}`}>
            <UsageMetricsChart data={data} />
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageAnalyticsDashboard;
